import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api, { waLink } from "../lib/api";
import useSeo from "../hooks/useSeo";
import { ChevronRight, MessageCircle, Scale, ArrowLeft, Check, Info } from "lucide-react";
import { toggleCompare, isInCompare } from "../lib/compareStore";
import { toast } from "sonner";
import QuoteWizard from "../components/QuoteWizard";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [inCompare, setInCompare] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (product?.id) {
      setInCompare(isInCompare(product.id));
      const h = () => setInCompare(isInCompare(product.id));
      window.addEventListener("compare:update", h);
      return () => window.removeEventListener("compare:update", h);
    }
  }, [product?.id]);

  useSeo({
    title: product ? `${product.name} — Otel Pro` : "Ürün — Otel Pro",
    description: product?.short_description,
    image: product?.images?.[0],
    jsonLd: product ? [{
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.short_description,
      image: product.images?.[0],
      sku: product.slug,
      brand: { "@type": "Brand", name: "Otel Pro" },
    }] : null,
  });

  if (!product) {
    return <Layout><div className="section-pad container-site">Yükleniyor...</div></Layout>;
  }

  const badgeLabels = { premium: "Premium", logo_baskili: "Logo Baskılı", toptan: "Toptan", stokta: "Stokta" };
  const waMsg = `Merhaba, "${product.name}" hakkında bilgi almak istiyorum.`;

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          {product.category && (
            <>
              <Link to={`/kategori/${product.category.slug}`} className="hover:text-navy">{product.category.name}</Link>
              <ChevronRight size={12} />
            </>
          )}
          <span className="text-navy font-medium line-clamp-1">{product.name}</span>
        </div>
      </div>

      <section className="section-pad !py-12" data-testid="product-detail">
        <div className="container-site grid md:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-softbg mb-3">
              {product.images?.[activeImg] && (
                <img src={product.images[activeImg]} alt={product.name} className="h-full w-full object-cover" />
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`h-16 w-16 rounded-md overflow-hidden border-2 ${i === activeImg ? "border-gold" : "border-transparent"}`}
                    data-testid={`product-gallery-thumb-${i}`}
                  >
                    <img src={im} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {(product.badges || []).map((b) => (
                <span key={b} className={`badge-${b === "logo_baskili" ? "logo" : b === "toptan" ? "toptan" : b === "stokta" ? "stock" : "premium"}`}>
                  {badgeLabels[b] || b}
                </span>
              ))}
            </div>
            <h1 className="heading-serif text-3xl md:text-4xl font-semibold text-navy leading-tight">
              {product.name}
            </h1>
            <p className="text-gray-600 mt-3 leading-relaxed">{product.short_description}</p>

            <div className="mt-6 border border-gold/30 rounded-lg p-5 bg-gold/5">
              <div className="flex items-center gap-2 text-gold font-medium text-sm">
                <Info size={14} /> Fiyat için teklif alın
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Minimum sipariş: <span className="text-navy font-medium">{product.min_order}</span> — Danışmanlarımız 24 saatte özel teklif hazırlar.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => setWizardOpen(true)}
                className="btn-gold flex-1 md:flex-none"
                data-testid="product-detail-quote-btn"
              >
                Teklif Al
              </button>
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noreferrer"
                className="btn-outline-gold"
                data-testid="product-detail-wa-btn"
              >
                <MessageCircle size={16} /> WhatsApp ile Sor
              </a>
              <button
                onClick={() => {
                  toggleCompare(product);
                  toast.success(inCompare ? "Karşılaştırmadan çıkarıldı" : "Karşılaştırmaya eklendi");
                }}
                className={`inline-flex items-center gap-1.5 px-4 py-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  inCompare ? "bg-teal text-white border-teal" : "border-gray-200 text-gray-600 hover:border-teal hover:text-teal"
                }`}
                data-testid="product-detail-compare-btn"
              >
                <Scale size={15} /> {inCompare ? "Karşılaştırmada" : "Karşılaştır"}
              </button>
            </div>

            {/* Advantages */}
            {product.features?.length > 0 && (
              <div className="mt-7">
                <h3 className="heading-serif text-lg font-semibold text-navy mb-3">Öne Çıkan Özellikler</h3>
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700">
                      <Check size={16} className="text-teal shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Description + Specs */}
      <section className="bg-softbg section-pad !py-14">
        <div className="container-site grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h3 className="heading-serif text-xl font-semibold text-navy mb-4">Uzman Yorumu</h3>
            <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
              {product.description}
            </div>
          </div>
          {product.specs && Object.keys(product.specs).length > 0 && (
            <div>
              <h3 className="heading-serif text-xl font-semibold text-navy mb-4">Teknik Özellikler</h3>
              <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                {Object.entries(product.specs).map(([k, v], i) => (
                  <div key={k} className={`flex justify-between text-sm p-3 ${i % 2 ? "bg-softbg" : "bg-white"}`}>
                    <span className="text-gray-500">{k}</span>
                    <span className="font-medium text-navy">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="container-site py-8">
        <Link to="/kategoriler" className="inline-flex items-center gap-1 text-navy hover:text-gold">
          <ArrowLeft size={14} /> Kategoriye Dön
        </Link>
      </div>

      <QuoteWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialSource="product_detail"
        initialItems={[{ product_id: product.id, name: product.name, quantity: 1 }]}
        initialNotes={`İlgilenilen ürün: ${product.name}`}
      />
    </Layout>
  );
}
